import React, { useState, useEffect, useRef } from "react";
import { notify } from "common-ui-components/components/notification/Notification";
import { Failure } from "common-ui-components/components/notification/templates";
import styles from "./MultiSelectTextInput.module.css";

/**
 * A multi-select text input component, which allows users to enter multiple text values.
 *
 * @param {string} textType The type of the text input (e.g. "emails", "urls").
 * @param {string[]} textArr An array of existing text values.
 * @param {string} placeHolderText The placeholder text to be displayed in the input field.
 * @param {function} handleAddText A function to be called when a new text value is added.
 * @param {function} handleRemoveText A function to be called when a text value is removed.
 * @param {boolean} needRemoveButton Whether to display a remove button for each text value.
 * @param {boolean} makeSelectedListVertical Whether to make the selected list vertical.
 * @param {boolean} inlineInput Whether to display the input field inline.
 * @param {function} updateSearchText A function to be called when the search text is updated.
 * @param {string} searchWidth The width of the search input field.
 * @param {boolean} single Whether to allow single select or not.
 *
 * @returns {React.Element} The rendered multi-select text input component.
 */
const MultiSelectTextInput = ({
  textType,
  textArr,
  placeHolderText,
  handleAddText,
  handleRemoveText,
  needRemoveButton = true,
  makeSelectedListVertical = false,
  inlineInput = false,
  updateSearchText,
  searchWidth = null,
  single = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const [highlightedIndex, setHighlightedIndex] = useState(null);

  const textContainerRef = useRef(null);
  const inputRef = useRef(null);

  const [searchForSingleSelect, setSearchForSingleSelect] = useState(single);

  useEffect(() => {
    if (single) setSearchForSingleSelect(textArr.length === 0);
  }, [textArr, single]);

  const inlineStyles = {
    container: {
      border: !searchForSingleSelect
        ? isFocused
          ? "1px solid #007bff"
          : "1px solid #AFB6BC"
        : "none",
      padding: "5px",
    },
    emailTag: {
      ...(makeSelectedListVertical && { width: "100%" }),
    },
    emailContainer: {
      ...(textArr.length > 0 && { width: "100%" }),
    },
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);

    if (highlightedIndex !== null) {
      setHighlightedIndex(null);
    }

    if (updateSearchText) {
      updateSearchText(e.target.value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Backspace") {
      if (inputValue === "" && textArr.length > 0) {
        const lastIndex = textArr.length - 1;

        if (highlightedIndex === lastIndex) {
          handleRemoveText(textType, lastIndex);
          setHighlightedIndex(null);
        } else {
          setHighlightedIndex(lastIndex);
        }
      }
      return;
    }

    if (highlightedIndex !== null) {
      setHighlightedIndex(null);
    }

    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      e.preventDefault();
      const newText = inputValue.trim().replace(/,$/, "");

      if (!textArr.includes(newText)) {
        if (handleAddText) {
          if (handleAddText(textType, newText)) {
            setInputValue("");
          }
          inputRef.current?.focus();
        }
      } else {
        notify(<Failure>Entry already exists</Failure>);
      }
    }
  };

  useEffect(() => {
    if (textContainerRef.current) {
      textContainerRef.current.scrollTop =
        textContainerRef.current.scrollHeight;
    }
  }, [textArr]);

  return (
    <div
      className={styles.container}
      style={{
        ...inlineStyles.container,
        width: searchWidth,
      }}
      onClick={() => inputRef.current?.focus()}
    >
      <div
        ref={textContainerRef}
        className={styles.emailContainer}
        style={inlineStyles.emailContainer}
      >
        {textArr.map((email, index) => {
          return(
            <div
              key={index}
              className={`${styles.emailTag} ${
                highlightedIndex === index ? styles.highlightedTag : ""
              }`}
              style={{ ...inlineStyles.emailTag }}
            >
              <span className={styles.emailText} style={inlineStyles.emailText}>
                {email}
              </span>
              {needRemoveButton && (
                <span
                  className={styles.removeButton}
                  style={inlineStyles.removeButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveText(textType, index);
                    setHighlightedIndex(null);
                    inputRef.current?.focus();
                  }}
                >
                  <span>&times;</span>
                </span>
              )}
            </div>
          )
        })}
        {(searchForSingleSelect || !single) && (
          <input
            type="text"
            value={inputValue}
            ref={inputRef}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeHolderText}
            onFocus={() => {
              // updateSearchText && updateSearchText(inputValue);
              setIsFocused(true);
            }}
            onBlur={() => {
              setIsFocused(false);
              setHighlightedIndex(null);
            }}
            className={styles.input}
            style={inlineStyles.input}
          />
        )}
      </div>
    </div>
  );
};

export default MultiSelectTextInput;
